import React, { useState, useRef, useEffect, MouseEvent } from 'react'
import { useInView } from 'react-intersection-observer'
import { createGolNonContentDataAttributes } from '../../../helpers/tracking'
import * as Styled from './video-renderer.styles.js'
import { CustomVideoProps } from './types'

const CustomVideo = ({ videoLink, fullControls = true, type }: CustomVideoProps): React.ReactElement => {
  const videoSrc = `${videoLink}#t=0.001`
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [userInitiatedPlay, setUserInitiatedPlay] = useState(false)

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.5
  })

  const addGolTracking = () => {
    const dataAttributes = createGolNonContentDataAttributes('link', 'initiate play', 'video', `${type}`, 'interaction')
    Object.keys(dataAttributes).forEach((attribute) =>
      videoRef.current?.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const togglePlay = (e: MouseEvent<HTMLElement>) => {
    if (videoRef.current) {
      e.preventDefault()
      if (videoRef.current.paused) {
        videoRef.current.play()
        setIsPlaying(true)
        addGolTracking()
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  useEffect(() => {
    const handlePlayEvent = () => {
      if (!userInitiatedPlay && fullControls && videoRef.current) {
        setUserInitiatedPlay(true)
        addGolTracking()
      }
    }

    if (videoRef.current) {
      videoRef.current.addEventListener('play', handlePlayEvent)
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('play', handlePlayEvent)
      }
    }
  }, [])

  useEffect(() => {
    if (inView && videoRef.current && !fullControls) {
      videoRef?.current?.play()
      setIsPlaying(true)
      setTimeout(() => {
        videoRef?.current?.pause()
        setIsPlaying(false)
      }, 5000)
    }
  }, [inView])

  if (!videoLink) return null

  return (
    <Styled.VideoWrapper data-testid='video-wrapper' data-test='video-wrapper' ref={inViewRef}>
      <Styled.VideoPlayer
        data-testid='video-player'
        ref={videoRef}
        {...(fullControls && { controls: true })}
        muted='muted'
        loop
        playsInline
        type='video/mp4'
        src={videoSrc}
      />
      {!fullControls && (
        <Styled.PlayPauseButton
          data-testid='play-pause-button'
          onClick={togglePlay}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Styled.PauseBtn /> : <Styled.PlayBtn />}
        </Styled.PlayPauseButton>
      )}
    </Styled.VideoWrapper>
  )
}

export default CustomVideo
